import { useEffect, useMemo, useState } from 'react';
import { getAthletes, mergeAthleteRecords } from '../../api/client';
import { useLoad } from '../../hooks/useLoad';
import { inlineError } from '../../hooks/useApiCall';
import { useToast } from '../../hooks/useToast';
import { useBusy } from '../../hooks/useBusy';
import { useI18n } from '../../i18n';
import { successText, useLabels } from '../../i18n/labels';
import { personName, nameMatches } from '../../utils/personName';
import ModalBackdrop from './ModalBackdrop';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import type { AthleteEntry, AthleteMergeResult } from '../../types';
import Notice from './Notice';
import { BADGE_XS } from './badgeSize';

/**
 * #578 — merge two athlete records of one person (admin only).
 *
 * The edit modal of a record opens it. The staff member finds the other
 * record of the same person (also an archived one) and selects the record to
 * keep. Each choice runs a dry run: the server checks and moves, then rolls
 * back. So the modal shows what moves, or why the server refuses, before
 * "Merge". The other record is deleted: a merge cannot be undone.
 */
interface Props {
  athlete: AthleteEntry;
  onMerged: (keptId: string, removedId: string) => void;
  onClose: () => void;
}

export default function MergeAthleteModal({ athlete, onMerged, onClose }: Props) {
  const { t } = useI18n();
  const m = t.athletes.merge;
  const { genderLabel } = useLabels();
  const { showSuccess } = useToast();
  const [query, setQuery] = useState('');
  const [other, setOther] = useState<AthleteEntry | null>(null);
  const [keepOther, setKeepOther] = useState(true);
  const [preview, setPreview] = useState<AthleteMergeResult | null>(null);
  const [error, setError] = useState('');
  // #605, #664 — the merge deletes a record: a second tap sends no second merge.
  const merging = useBusy();

  // Every record, the archived and the linked ones too: the other record is
  // often archived. A linked record is `source: 'user'`, with the id of its
  // record; the server applies the rules of the account link.
  const { data: records } = useLoad(
    () => getAthletes({ includeInactive: true }).then(r => r.data.filter(a => a.id !== athlete.id)),
    [athlete.id],
    { context: 'MergeAthleteModal:athletes', initial: [] as AthleteEntry[], expected: () => true, onError: e => setError(inlineError(e)) },
  );

  const matches = useMemo(
    () => (query.trim() ? records.filter(a => nameMatches(query, a.first_name, a.last_name)).slice(0, 8) : []),
    [records, query],
  );

  const removed = other ? (keepOther ? athlete : other) : null;
  const kept = other ? (keepOther ? other : athlete) : null;

  // A dry run for each choice: what moves, or why the server refuses.
  useEffect(() => {
    if (!removed || !kept) return;
    let live = true;
    setPreview(null);
    setError('');
    mergeAthleteRecords(removed.id, kept.id, true)
      .then(r => { if (live) setPreview(r.data); })
      .catch(err => { if (live) setError(inlineError(err)); });
    return () => { live = false; };
  }, [removed?.id, kept?.id]);

  // A new choice drops the old preview at once: Merge stays off until the
  // dry run of the new pair answers.
  function choose(keepTheOther: boolean) {
    setPreview(null);
    setKeepOther(keepTheOther);
  }

  function pick(a: AthleteEntry) {
    setPreview(null);
    setOther(a);
    // The default keeps the active record: the archived one is usually the old copy.
    setKeepOther(!(a.is_active === false && athlete.is_active !== false));
    setQuery('');
  }

  async function merge() {
    if (!removed || !kept) return;
    await merging.run(async () => {
      setError('');
      try {
        const r = await mergeAthleteRecords(removed.id, kept.id);
        showSuccess(successText(r.data));
        onMerged(kept.id, removed.id);
      } catch (err) {
        setError(inlineError(err));
      }
    });
  }

  const rows: Array<[string, (a: AthleteEntry) => string]> = [
    [m.name, a => personName(a.first_name, a.last_name)],
    [m.birthDate, a => a.birth_date ?? '—'],
    [m.gender, a => (a.gender ? genderLabel(a.gender) : '—')],
    [m.team, a => a.team_name ?? '—'],
    [m.status, a => (a.is_active === false ? m.archived : m.active)],
    [m.experience, a => String(a.experience_bouts ?? 0)],
  ];

  return (
    <ModalBackdrop onClose={onClose} maxWidth="lg" ariaLabel={m.aria}>
      <ModalHeader title={m.title} subtitle={personName(athlete.first_name, athlete.last_name)} />
      <div className="space-y-4">
        <div>
          <input
            className="input" data-autofocus data-testid="merge-search"
            placeholder={m.search} value={query} onChange={e => setQuery(e.target.value)}
          />
          {query.trim() && (
            <ul className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-100">
              {matches.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">{m.noMatch}</li>}
              {matches.map(a => (
                <li key={a.id}>
                  <button type="button" onClick={() => pick(a)} data-testid={`merge-candidate-${a.id}`}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">
                    <span className="font-medium">{personName(a.first_name, a.last_name)}</span>
                    <span className="text-gray-500"> · {a.birth_date ?? '—'} · {a.team_name ?? '—'}</span>
                    {a.is_active === false && <span className={`badge badge-gray ${BADGE_XS} ml-2`}>{m.archived}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {other && (
          <table className="w-full text-sm" data-testid="merge-compare">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1 pr-2" />
                {[{ a: athlete, label: m.thisRecord, keepIt: !keepOther, set: () => choose(false), id: 'merge-keep-this' },
                  { a: other, label: m.otherRecord, keepIt: keepOther, set: () => choose(true), id: 'merge-keep-other' }].map(c => (
                  <th key={c.id} className="py-1 pr-2 font-medium">
                    <div>{c.label}</div>
                    <label className="inline-flex items-center gap-1 font-normal text-gray-700">
                      <input type="radio" name="merge-keep" checked={c.keepIt} onChange={c.set} data-testid={c.id} />
                      {m.keep}
                    </label>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, value]) => (
                <tr key={label} className="border-t border-gray-100">
                  <td className="py-1 pr-2 text-gray-500">{label}</td>
                  <td className="py-1 pr-2">{value(athlete)}</td>
                  <td className="py-1 pr-2">{value(other)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {other && !preview && !error && <p className="text-sm text-gray-500">{m.checking}</p>}
        {preview && (
          <div data-testid="merge-moves">
            <p className="text-sm font-medium text-gray-700">{m.moves}</p>
            <ul className="text-sm text-gray-700 list-disc pl-5">
              <li>{m.registrations.replace('{n}', String(preview.moved.registrations))}</li>
              <li>{m.bouts.replace('{n}', String(preview.moved.bouts))}</li>
              <li>{m.seeds.replace('{n}', String(preview.moved.seeds))}</li>
              <li>{m.requests.replace('{n}', String(preview.moved.requests))}</li>
              {preview.moved.account_link && <li>{m.accountLink}</li>}
            </ul>
            <p className="mt-2 text-sm text-amber-700">{m.warning}</p>
          </div>
        )}
        {error && <Notice tone="error" plain data-testid="merge-error">{error}</Notice>}
      </div>
      <ModalFooter onSave={merge} busy={merging.busy} saveLabel={m.confirm} saveDisabled={!preview} onCancel={onClose} />
    </ModalBackdrop>
  );
}
